import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SideMenu from '../components/SideMenu';
import { useTheme } from '../context/ThemeContext';
import { useProducts } from '../context/ProductsContext';
import './webhooks.css';


function formatDimensions(l, w, h) {
  if (l == null && w == null && h == null) return '—';
  const fmt = (v) => (v == null ? '—' : String(v));
  return `${fmt(l)} × ${fmt(w)} × ${fmt(h)}`;
}

function formatWeight(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

function formatShape(product, t) {
  const shape = String(product?.shape || '').toLowerCase();
  if (shape === 'cylinder' || product?.raw?.cylinder === true) {
    return t('productShapeCylinder');
  }
  if (shape === 'box') {
    return t('productShapeBox');
  }
  return shape || '—';
}

function PaginationBar({
  theme,
  t,
  pageSize,
  setPageSize,
  total,
  safePage,
  totalPages,
  setPage,
}) {
  if (total <= 0) {
    return null;
  }

  return (
    <div className="webhooks-paginationBar webhooks-sectionGap">
      <div className="webhooks-fieldLabel">{t('Items per page')}</div>
      <select
        className={['webhooks-select', theme].join(' ')}
        value={pageSize}
        onChange={(e) => setPageSize(Number(e.target.value) || 7)}
        style={{
          width: 'auto',
          minWidth: 72,
          colorScheme: theme === 'dark' ? 'dark' : 'light',
        }}
      >
        {[7, 10, 20, 50, 100].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      <div className="webhooks-muted">
        {t('total')}: {total}
      </div>
      <button
        type="button"
        className={['webhooks-secondaryBtn', theme, safePage <= 1 ? 'disabled' : 'enabled'].join(' ')}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={safePage <= 1}
      >
        {t('Prev')}
      </button>
      <span className="webhooks-muted">
        {t('Page')} {safePage} {t('of')} {totalPages}
      </span>
      <button
        type="button"
        className={[
          'webhooks-secondaryBtn',
          theme,
          safePage >= totalPages ? 'disabled' : 'enabled',
        ].join(' ')}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={safePage >= totalPages}
      >
        {t('Next')}
      </button>
    </div>
  );
}

function usePagedRows(items, pageSize, page, setPage) {
  const allRows = useMemo(() => {
    return (Array.isArray(items) ? items : []).map((item, idx) => ({
      id: item?.id ?? `${idx}`,
      raw: item,
    }));
  }, [items]);

  const total = allRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return allRows.slice(start, start + pageSize);
  }, [allRows, safePage, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [items, pageSize, setPage]);

  return { allRows, pageRows, total, totalPages, safePage };
}

export default function Packaging() {
  const { currentTheme, theme } = useTheme();
  const { t } = useTranslation();
  const {
    products,
    boxes,
    countryCode,
    refreshProducts,
    loading,
    error,
    boxesError,
  } = useProducts();

  const [productPageSize, setProductPageSize] = useState(7);
  const [productPage, setProductPage] = useState(1);
  const [boxPageSize, setBoxPageSize] = useState(7);
  const [boxPage, setBoxPage] = useState(1);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  const productsPaging = usePagedRows(products, productPageSize, productPage, setProductPage);
  const boxesPaging = usePagedRows(boxes, boxPageSize, boxPage, setBoxPage);
  const hasCountry = Boolean(countryCode);

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu currentTheme={currentTheme} theme={theme} />

      <main className="webhooks-main">
        <h1 className="webhooks-title">{t('packaging')}</h1>

        {!hasCountry && (
          <div className="webhooks-muted webhooks-tableEmpty">
            {t('packagingSelectCountry')}
          </div>
        )}

        {hasCountry && loading && (
          <div className="webhooks-muted">{t('loading')}</div>
        )}

        {hasCountry && !loading && (
          <>
            <div className="webhooks-tableWrap">
              <div className={`webhooks-tableTopbar ${theme}`}>
                <div className="webhooks-tableTopbarTitle">{t('packagingProductsPanel')}</div>
                <button
                  type="button"
                  onClick={() => refreshProducts()}
                  disabled={loading}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    loading ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {loading ? `${t('loading')}...` : t('refresh')}
                </button>
              </div>

              {error && (
                <div className={`webhooks-error ${theme}`} style={{ margin: '12px' }}>
                  {t(error)}
                </div>
              )}

              {productsPaging.allRows.length > 0 ? (
                <table className="webhooks-table">
                  <thead>
                    <tr>
                      <th className="webhooks-th">{t('productSku')}</th>
                      <th className="webhooks-th">{t('productName')}</th>
                      <th className="webhooks-th">{t('productDimensions')}</th>
                      <th className="webhooks-th">{t('productWeightKg')}</th>
                      <th className="webhooks-th">{t('productShape')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productsPaging.pageRows.map((row, idx) => {
                      const product = row.raw || {};
                      return (
                        <tr
                          key={row.id}
                          className={idx === productsPaging.pageRows.length - 1 ? 'webhooks-trLast' : ''}
                        >
                          <td className="webhooks-td">{product.sku || '—'}</td>
                          <td className="webhooks-td">{product.name || '—'}</td>
                          <td className="webhooks-td">
                            {formatDimensions(product.length_cm, product.width_cm, product.height_cm)}
                          </td>
                          <td className="webhooks-td">{formatWeight(product.weight_kg)}</td>
                          <td className="webhooks-td">{formatShape(product, t)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                !error && (
                  <div className="webhooks-muted webhooks-tableEmpty">
                    {t('productsEmpty')}
                  </div>
                )
              )}
            </div>

            <PaginationBar
              theme={theme}
              t={t}
              pageSize={productPageSize}
              setPageSize={setProductPageSize}
              total={productsPaging.total}
              safePage={productsPaging.safePage}
              totalPages={productsPaging.totalPages}
              setPage={setProductPage}
            />

            <div className="webhooks-tableWrap webhooks-sectionGap">
              <div className={`webhooks-tableTopbar ${theme}`}>
                <div className="webhooks-tableTopbarTitle">{t('packagingBoxesPanel')}</div>
                <button
                  type="button"
                  onClick={() => refreshProducts()}
                  disabled={loading}
                  className={[
                    'webhooks-secondaryBtn',
                    theme,
                    loading ? 'disabled' : 'enabled',
                  ].join(' ')}
                >
                  {loading ? `${t('loading')}...` : t('refresh')}
                </button>
              </div>

              {boxesError && (
                <div className={`webhooks-error ${theme}`} style={{ margin: '12px' }}>
                  {t(boxesError)}
                </div>
              )}

              {boxesPaging.allRows.length > 0 ? (
                <table className="webhooks-table">
                  <thead>
                    <tr>
                      <th className="webhooks-th">{t('boxName')}</th>
                      <th className="webhooks-th">{t('boxInnerDimensions')}</th>
                      <th className="webhooks-th">{t('boxOuterDimensions')}</th>
                      <th className="webhooks-th">{t('boxVolume')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boxesPaging.pageRows.map((row, idx) => {
                      const box = row.raw || {};
                      return (
                        <tr
                          key={row.id}
                          className={idx === boxesPaging.pageRows.length - 1 ? 'webhooks-trLast' : ''}
                        >
                          <td className="webhooks-td">{box.name || '—'}</td>
                          <td className="webhooks-td">
                            {formatDimensions(
                              box.inner_length_cm,
                              box.inner_width_cm,
                              box.inner_height_cm,
                            )}
                          </td>
                          <td className="webhooks-td">
                            {formatDimensions(
                              box.outer_length_cm,
                              box.outer_width_cm,
                              box.outer_height_cm,
                            )}
                          </td>
                          <td className="webhooks-td">{formatWeight(box.volume)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                !boxesError && (
                  <div className="webhooks-muted webhooks-tableEmpty">
                    {t('boxesEmpty')}
                  </div>
                )
              )}
            </div>

            <PaginationBar
              theme={theme}
              t={t}
              pageSize={boxPageSize}
              setPageSize={setBoxPageSize}
              total={boxesPaging.total}
              safePage={boxesPaging.safePage}
              totalPages={boxesPaging.totalPages}
              setPage={setBoxPage}
            />
          </>
        )}
      </main>
    </div>
  );
}
